import { Fragment, lazy, memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { ExternalComponentsDashboard } from 'basic-components';
import { Breadcrumb, Fetching } from 'primary-components';

import { settingsMyAccountActions } from 'actions/modules/myAccount';
import { dataSharingHooks } from 'hooks/modules';
import { allBreadcrumbs } from 'resources/constants/allBreadcrumbs';
import properties from 'resources/constants/properties.json';
import { routing } from 'routing';
import { getPathByAccess } from 'services/getPathByAccess';

import ErrorBoundaryLocal from 'components/App/ErrorBoundary';
import { SideBar } from 'primary/SideBar';

import Tools from './Tools';

import classNames from 'classnames';

const AITools = lazy(() => import('components/modules/AITools'))
const Datalake = lazy(() => import('components/modules/Datalake'))
const DataScience = lazy(() => import('components/modules/DataScience'))
const DataStorage = lazy(() => import('components/modules/DataStorage'))
const Insight = lazy(() => import('components/modules/Insight'))
const Logs = lazy(() => import('components/external/Logs'))
const MyAccount = lazy(() => import('components/modules/MyAccount'))
const Trash = lazy(() => import('components/modules/Trash'))

const DesktopRouting = ({ isIframe = false }) => {
  const dispatch = useDispatch(),
        navigate = useNavigate(),
        defaultPage = useSelector(state => state.myAccountState.settingsState.defaultPage)

  useEffect(() => {
    // dispatch(loginActions.logoutAllTabs())
    dispatch(settingsMyAccountActions.onSettingChanged())
  }, [dispatch])

  dataSharingHooks.useProviders()

  return (
    <Routes>
      <Route
          element={<ExternalComponentsDashboard/>}
          ErrorBoundary={ErrorBoundaryLocal}
          path={`/${dispatch(getPathByAccess(routing.templates.name))}/*`}
      />
      <Route 
          ErrorBoundary={ErrorBoundaryLocal}
          path={`/${dispatch(getPathByAccess(routing.logs.name))}/:jobId`}
      >
        <Route 
            element={<Logs/>}
            ErrorBoundary={ErrorBoundaryLocal}
            path={`:component`}
        />
        <Route 
            element={<Logs/>}
            ErrorBoundary={ErrorBoundaryLocal}
            path={`:component/:level`}
        />
        <Route 
            element={<Logs/>}
            ErrorBoundary={ErrorBoundaryLocal}
            path={``}
        />
      </Route>
      <Route
          element={defaultPage ? (
            <div className={classNames("root", { "root--is-iframe": isIframe })}>
              {!isIframe && <SideBar/>}
              <div className="content"> 
                {!isIframe && (
                  <Breadcrumb 
                      items={allBreadcrumbs}
                      navigate={navigate}
                  />
                )}
                <Outlet/>
                {!isIframe && (
                  <Tools/>
                )}
              </div>
            </div>
          ) : <Fetching isFetching/>}
          ErrorBoundary={ErrorBoundaryLocal}
          path="/"
      >
          <Route 
              element={<AITools/>}
              ErrorBoundary={ErrorBoundaryLocal}
              path={`${dispatch(getPathByAccess(routing.aiTools.name))}/*`}
          />
          <Route 
              element={<MyAccount/>}
              ErrorBoundary={ErrorBoundaryLocal}
              path={isIframe ? undefined : `${routing.myAccount.name}/*`}
          />
          <Route 
              element={<Datalake/>}
              ErrorBoundary={ErrorBoundaryLocal}
              path={`${dispatch(getPathByAccess(routing.dataLake.name))}/*`}
          />
          <Route 
              element={<DataStorage/>}
              ErrorBoundary={ErrorBoundaryLocal}
              path={`${dispatch(getPathByAccess(routing.dataStorage.name))}/*`}
          />
          <Route 
              element={<DataScience/>}
              ErrorBoundary={ErrorBoundaryLocal}
              path={`${dispatch(getPathByAccess(routing.dataScience.name))}/*`}
          />
          <Route 
              element={<Insight/>}
              ErrorBoundary={ErrorBoundaryLocal}
              path={`${dispatch(getPathByAccess(routing.insight.name))}/*`}
          />
          <Route 
              element={properties.warehouse_only === "true" || properties.warehouse_hybrid === "true" ? <Trash/> : <Fragment/>}
              ErrorBoundary={ErrorBoundaryLocal}
              path={properties.warehouse_only === "true" || properties.warehouse_hybrid === "true" ? `${routing.trash.name}/*` : undefined}
          />
          <Route
              element={(
                <Navigate to={isIframe ? routing.insight.name : dispatch(getPathByAccess(defaultPage)) || routing.myAccount.name}/>
              )}
              ErrorBoundary={ErrorBoundaryLocal}
              path="*"
          />
      </Route>
    </Routes>
  )
}

DesktopRouting.propTypes = {
  isIframe: PropTypes.bool
}

export default memo(DesktopRouting);
